import { houseIdSchema, remoteConfigOverrideSchema } from "@nano/game-config";
import { z } from "zod";
import { avatarIdSchema, cosmeticIdSchema, equipmentSchema } from "./avatar.ts";
import { errorCodeSchema } from "./errors.ts";
import { levelProofSchema } from "./proofs.ts";

export const API_PREFIX = "/api/v1";

/** Requests above this size are rejected before parsing. */
export const MAX_REQUEST_BYTES = 96 * 1024;
export const MAX_SYNC_RESULTS = 10;

const uuidSchema = z.string().uuid();
const starsSchema = z.union([z.literal(1), z.literal(2), z.literal(3)]);
const levelIdSchema = z.number().int().min(1).max(100);

export const sessionSchema = z.object({
  token: z.string().min(32).max(128),
  expiresAt: z.number().int(),
});
export type Session = z.infer<typeof sessionSchema>;

export const houseProgressSchema = z.object({
  houseId: houseIdSchema,
  highestCompleted: z.number().int().min(0).max(100),
  completedCount: z.number().int().min(0).max(100),
  starsTotal: z.number().int().min(0).max(300),
});
export type HouseProgress = z.infer<typeof houseProgressSchema>;

const timeMsSchema = z.number().int().positive().nullable();

export const playerLevelSchema = z.object({
  houseId: houseIdSchema,
  levelId: levelIdSchema,
  bestStars: starsSchema,
  /** Fastest plausible completion (client clock); for future time rankings only. */
  bestTimeMs: timeMsSchema.optional(),
});
export type PlayerLevel = z.infer<typeof playerLevelSchema>;

export const walletSchema = z.object({
  gold: z.number().int().nonnegative(),
  totalStars: z.number().int().nonnegative(),
  totalCompleted: z.number().int().nonnegative(),
  revision: z.number().int().nonnegative(),
});
export type Wallet = z.infer<typeof walletSchema>;

export const playerSnapshotSchema = z.object({
  playerId: z.string().min(8),
  publicTag: z.string().min(4).max(16),
  displayName: z.string().min(1).max(40),
  avatarId: avatarIdSchema,
  wallet: walletSchema,
  equipped: equipmentSchema,
  owned: z.array(cosmeticIdSchema),
  houses: z.array(houseProgressSchema),
  levels: z.array(playerLevelSchema),
  hasRecoveryCode: z.boolean(),
  /** Achievement ids whose gold the server has paid (older Workers omit it). */
  claimedAchievements: z.array(z.string().max(48)).default([]),
  serverTime: z.number().int(),
});
export type PlayerSnapshot = z.infer<typeof playerSnapshotSchema>;

// --- guest -----------------------------------------------------------------

export const guestCreateRequestSchema = z
  .object({
    localGuestId: uuidSchema.optional(),
    avatarId: avatarIdSchema.optional(),
    displayName: z.string().max(60).optional(),
    deviceLabel: z.string().max(40).optional(),
  })
  .strict();
export type GuestCreateRequest = z.infer<typeof guestCreateRequestSchema>;

export const authResponseSchema = z.object({
  session: sessionSchema,
  snapshot: playerSnapshotSchema,
});
export type AuthResponse = z.infer<typeof authResponseSchema>;

// --- recovery --------------------------------------------------------------

export const recoveryIssueResponseSchema = z.object({
  code: z.string().min(20).max(32),
  issuedAt: z.number().int(),
});
export type RecoveryIssueResponse = z.infer<typeof recoveryIssueResponseSchema>;

export const recoveryRestoreRequestSchema = z
  .object({
    code: z.string().min(16).max(40),
    deviceLabel: z.string().max(40).optional(),
  })
  .strict();
export type RecoveryRestoreRequest = z.infer<typeof recoveryRestoreRequestSchema>;

// --- progress sync -----------------------------------------------------------

export const syncResultInputSchema = z
  .object({
    houseId: houseIdSchema,
    levelId: levelIdSchema,
    contentVersion: z.string().min(1).max(32),
    proof: levelProofSchema,
  })
  .strict()
  .refine(
    (result) =>
      result.proof.houseId === result.houseId &&
      result.proof.levelId === result.levelId &&
      result.proof.contentVersion === result.contentVersion,
    { message: "proof header must match result header" },
  );
export type SyncResultInput = z.infer<typeof syncResultInputSchema>;

export const syncRequestSchema = z
  .object({
    clientMutationId: uuidSchema,
    baseRevision: z.number().int().nonnegative().optional(),
    results: z.array(syncResultInputSchema).min(1).max(MAX_SYNC_RESULTS),
  })
  .strict();
export type SyncRequest = z.infer<typeof syncRequestSchema>;

export const syncOutcomeSchema = z.object({
  houseId: houseIdSchema,
  levelId: levelIdSchema,
  status: z.enum(["accepted", "duplicate", "rejected"]),
  reason: errorCodeSchema.optional(),
  stars: starsSchema.optional(),
  bestStars: starsSchema.optional(),
  goldAwarded: z.number().int().nonnegative(),
  firstClear: z.boolean(),
  timeMs: timeMsSchema.optional(),
  bestTimeMs: timeMsSchema.optional(),
});
export type SyncOutcome = z.infer<typeof syncOutcomeSchema>;

export const syncResponseSchema = z.object({
  results: z.array(syncOutcomeSchema),
  wallet: walletSchema,
  houses: z.array(houseProgressSchema),
});
export type SyncResponse = z.infer<typeof syncResponseSchema>;

// --- leaderboard -------------------------------------------------------------

export const leaderboardEntrySchema = z.object({
  rank: z.number().int().positive(),
  publicTag: z.string(),
  displayName: z.string(),
  avatarId: avatarIdSchema,
  totalStars: z.number().int().nonnegative(),
  totalCompleted: z.number().int().nonnegative(),
});
export type LeaderboardEntry = z.infer<typeof leaderboardEntrySchema>;

export const leaderboardResponseSchema = z.object({
  entries: z.array(leaderboardEntrySchema),
  generatedAt: z.number().int(),
  cacheSeconds: z.number().int().positive(),
});
export type LeaderboardResponse = z.infer<typeof leaderboardResponseSchema>;

export const leaderboardMeResponseSchema = z.object({
  /** null when the player has no stars yet or sits beyond the scan cap. */
  rank: z.number().int().positive().nullable(),
  beyondCap: z.boolean(),
  totalStars: z.number().int().nonnegative(),
  totalCompleted: z.number().int().nonnegative(),
  houses: z.array(houseProgressSchema),
  generatedAt: z.number().int(),
});
export type LeaderboardMeResponse = z.infer<typeof leaderboardMeResponseSchema>;

// --- shop & avatar -------------------------------------------------------------

export const purchaseRequestSchema = z
  .object({
    itemId: cosmeticIdSchema,
    idempotencyKey: uuidSchema,
    equip: z.boolean().default(true),
  })
  .strict();
export type PurchaseRequest = z.input<typeof purchaseRequestSchema>;

export const purchaseResponseSchema = z.object({
  itemId: cosmeticIdSchema,
  replayed: z.boolean(),
  wallet: walletSchema,
  owned: z.array(cosmeticIdSchema),
  equipped: equipmentSchema,
});
export type PurchaseResponse = z.infer<typeof purchaseResponseSchema>;

export const equipRequestSchema = z
  .object({
    equipped: equipmentSchema,
  })
  .strict();
export type EquipRequest = z.infer<typeof equipRequestSchema>;

export const equipResponseSchema = z.object({
  equipped: equipmentSchema,
  revision: z.number().int().nonnegative(),
});
export type EquipResponse = z.infer<typeof equipResponseSchema>;

export const profilePatchRequestSchema = z
  .object({
    displayName: z.string().max(60).optional(),
    avatarId: avatarIdSchema.optional(),
  })
  .strict()
  .refine((patch) => patch.displayName !== undefined || patch.avatarId !== undefined, {
    message: "nothing to update",
  });
export type ProfilePatchRequest = z.infer<typeof profilePatchRequestSchema>;

export const profileResponseSchema = z.object({
  displayName: z.string(),
  avatarId: avatarIdSchema,
  equipped: equipmentSchema,
  revision: z.number().int().nonnegative(),
});
export type ProfileResponse = z.infer<typeof profileResponseSchema>;

// --- hints ----------------------------------------------------------------------

/** One extra hint for a level (after the free ones); the price comes from the server config. */
export const hintPurchaseRequestSchema = z
  .object({
    houseId: houseIdSchema,
    levelId: levelIdSchema,
    idempotencyKey: uuidSchema,
  })
  .strict();
export type HintPurchaseRequest = z.infer<typeof hintPurchaseRequestSchema>;

export const hintPurchaseResponseSchema = z.object({
  /** True when this key was already paid (a retried tap): nothing charged twice. */
  replayed: z.boolean(),
  priceGold: z.number().int().nonnegative(),
  wallet: walletSchema,
});
export type HintPurchaseResponse = z.infer<typeof hintPurchaseResponseSchema>;

// --- achievements --------------------------------------------------------------

export const achievementClaimRequestSchema = z
  .object({
    achievementId: z.string().min(2).max(48).regex(/^[a-z0-9-]+$/),
  })
  .strict();
export type AchievementClaimRequest = z.infer<typeof achievementClaimRequestSchema>;

export const achievementClaimResponseSchema = z.object({
  achievementId: z.string(),
  goldAwarded: z.number().int().nonnegative(),
  alreadyClaimed: z.boolean(),
  wallet: walletSchema,
  claimed: z.array(z.string()),
});
export type AchievementClaimResponse = z.infer<typeof achievementClaimResponseSchema>;

// --- data deletion --------------------------------------------------------------

/** DELETE /me: the player, their progress, wallet, items and recovery code are gone. */
export const deleteMeResponseSchema = z.object({ deleted: z.literal(true) });
export type DeleteMeResponse = z.infer<typeof deleteMeResponseSchema>;

// --- rewarded ads --------------------------------------------------------------

/**
 * What one rewarded ad pays for. The client names it in the ad's SSV custom
 * data; Google signs that string into its reward callback, so the Worker pays
 * the reward Google vouched for, never one the client describes later.
 *   gift:<nonce>                   one city gift chest
 *   double:<nonce>:<house>:<level> the level's first-clear gold once more
 */
export type AdReward =
  | { readonly kind: "gift"; readonly nonce: string }
  | { readonly kind: "double"; readonly nonce: string; readonly houseId: z.infer<typeof houseIdSchema>; readonly levelId: number };

export function adRewardData(reward: AdReward): string {
  return reward.kind === "gift" ? `gift:${reward.nonce}` : `double:${reward.nonce}:${reward.houseId}:${reward.levelId}`;
}

export function parseAdRewardData(data: string): AdReward | null {
  const [kind, nonce, houseId, level, ...rest] = data.split(":");
  if (rest.length > 0 || !uuidSchema.safeParse(nonce).success || nonce === undefined) return null;
  if (kind === "gift" && houseId === undefined) return { kind, nonce };
  const house = houseIdSchema.safeParse(houseId);
  const levelId = levelIdSchema.safeParse(Number(level));
  if (kind === "double" && house.success && levelId.success && String(levelId.data) === level) {
    return { kind, nonce, houseId: house.data, levelId: levelId.data };
  }
  return null;
}

export const adClaimRequestSchema = z
  .object({
    /** The custom data string the ad was loaded with (see adRewardData). */
    reward: z.string().min(10).max(100),
  })
  .strict();
export type AdClaimRequest = z.infer<typeof adClaimRequestSchema>;

export const adClaimResponseSchema = z.object({
  /**
   * granted: paid now. already: this ad (or this level's double) was paid before.
   * waiting: Google's callback or the level's sync has not landed yet, ask again soon.
   * capped: today's gift chests are all open.
   */
  status: z.enum(["granted", "already", "waiting", "capped"]),
  goldAwarded: z.number().int().nonnegative(),
  giftsLeftToday: z.number().int().nonnegative(),
  wallet: walletSchema,
});
export type AdClaimResponse = z.infer<typeof adClaimResponseSchema>;

export const adStatusResponseSchema = z.object({
  giftsLeftToday: z.number().int().nonnegative(),
  giftGold: z.number().int().nonnegative(),
});
export type AdStatusResponse = z.infer<typeof adStatusResponseSchema>;

// --- status ------------------------------------------------------------------

export const statusResponseSchema = z.object({
  config: remoteConfigOverrideSchema,
  maintenance: z.boolean(),
  serverTime: z.number().int(),
});
export type StatusResponse = z.infer<typeof statusResponseSchema>;
